const IMPERSONATION_SESSION_KEY = "sge.impersonation.session";
const IMPERSONATION_CONTEXT_KEY = "sge.impersonation.context";
const IMPERSONATION_RETURN_KEY = "sge.impersonation.return";

export interface ImpersonationContext {
  id: number;
  actorUserId: number;
  actorUserName: string;
  actorUserType: string;
  targetUserId: number;
  targetUserName: string;
  targetUserType: string;
  accountId: number | null;
  reason: string;
  startedAt: string;
  expiresAt: string;
}

interface ImpersonationReturn {
  companyId: string | null;
  path: string;
}

// Aceita apenas caminhos internos; rejeita protocol-relative (//host, /\host)
// e qualquer valor que resolva para outra origem antes de navegar.
const isSafeReturnPath = (value: unknown): value is string => {
  if (typeof value !== "string" || !value.startsWith("/")) return false;
  if (value.startsWith("//") || value.startsWith("/\\")) return false;
  try {
    return (
      new URL(value, window.location.origin).origin === window.location.origin
    );
  } catch {
    return false;
  }
};

const decodeSession = (stored: string | null): string | undefined => {
  if (!stored) return undefined;
  try {
    const parsed: unknown = JSON.parse(stored);
    return typeof parsed === "string" ? parsed : undefined;
  } catch {
    return stored;
  }
};

export const getAuthorization = (): string | undefined =>
  decodeSession(sessionStorage.getItem(IMPERSONATION_SESSION_KEY)) ??
  decodeSession(localStorage.getItem("s") ?? sessionStorage.getItem("s"));

export const readImpersonationContext = (): ImpersonationContext | null => {
  const stored = sessionStorage.getItem(IMPERSONATION_CONTEXT_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored) as Partial<ImpersonationContext>;
    if (
      typeof parsed.id !== "number" ||
      typeof parsed.actorUserName !== "string" ||
      typeof parsed.targetUserName !== "string" ||
      typeof parsed.expiresAt !== "string"
    ) {
      return null;
    }
    return parsed as ImpersonationContext;
  } catch {
    return null;
  }
};

export const beginImpersonation = (
  sessionId: string,
  context: ImpersonationContext
) => {
  const returnState: ImpersonationReturn = {
    companyId: localStorage.getItem("companyId"),
    path: `${window.location.pathname}${window.location.search}`,
  };
  sessionStorage.setItem(IMPERSONATION_SESSION_KEY, sessionId);
  sessionStorage.setItem(IMPERSONATION_CONTEXT_KEY, JSON.stringify(context));
  sessionStorage.setItem(IMPERSONATION_RETURN_KEY, JSON.stringify(returnState));
  localStorage.removeItem("companyId");
};

export const endImpersonationLocally = (): string => {
  let returnState: ImpersonationReturn = { companyId: null, path: "/contas" };
  const storedReturn = sessionStorage.getItem(IMPERSONATION_RETURN_KEY);
  if (storedReturn) {
    try {
      const parsed = JSON.parse(storedReturn) as Partial<ImpersonationReturn>;
      returnState = {
        companyId:
          typeof parsed.companyId === "string" ? parsed.companyId : null,
        path: isSafeReturnPath(parsed.path) ? parsed.path : "/contas",
      };
    } catch {
      // Usa o retorno seguro padrão.
    }
  }

  sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);
  sessionStorage.removeItem(IMPERSONATION_CONTEXT_KEY);
  sessionStorage.removeItem(IMPERSONATION_RETURN_KEY);
  if (returnState.companyId) {
    localStorage.setItem("companyId", returnState.companyId);
  } else {
    localStorage.removeItem("companyId");
  }
  return returnState.path;
};
